import React, { useContext, useCallback } from "react";
import { SnackbarQueueContext } from "contexts/SnackbarQueueContext";
import { UserProfileContext } from "contexts/UserProfileContext";
import { useFetchPostV2 } from "hooks/fetch/useFetchPostV2";
import { caseIdToDisplayId } from "core/utils/str";
import Tooltip from "@mui/material/Tooltip";
import { styled } from "@mui/material/styles";
import { Button } from "@mui/material";

export interface CaseActionRequestButtonProps {
  caseId: number;
  assignedUser: { id: number | undefined; name: string | null };
}

export const CaseActionRequestButton = ({
  caseId,
  assignedUser
}: CaseActionRequestButtonProps): JSX.Element => {
  const { addSnackbarToQueue } = useContext(SnackbarQueueContext);
  const { currentUserId } = useContext(UserProfileContext);
  const {
    postCallback: actionRequest,
    loading: actionRequestLoading
  } = useFetchPostV2(`cases/${caseId}/action_request/`);

  let tooltipTitle = "No user assigned";
  if (assignedUser?.id) {
    if (assignedUser?.id === currentUserId) {
      tooltipTitle = "Cannot request update from yourself";
    } else {
      tooltipTitle = `Request update from ${assignedUser.name}`;
    }
  }

  const onActionRequest = useCallback(() => {
    actionRequest().then(
      _fulfilled => {
        addSnackbarToQueue({
          message: `An email was sent to ${
            assignedUser?.name ? assignedUser.name : "the user"
          } requesting an update to case ${caseIdToDisplayId(caseId)}.`,
          severity: "success"
        });
      },
      error => {
        let message = "Failed due to internal server error.";
        if (error.status === 403) {
          message = `No user has been assigned to case ${caseIdToDisplayId(
            caseId
          )}.`;
        } else if (error.status === 503) {
          message = "Email notification failed to send while processing.";
        }
        addSnackbarToQueue({ message, severity: "error" });
      }
    );
  }, [actionRequest, addSnackbarToQueue, assignedUser, caseId]);

  return (
    <Tooltip title={tooltipTitle} placement="top" enterDelay={0}>
      <ButtonTooltipArea>
        <Button
          variant="contained"
          color="primary"
          size="small"
          disabled={
            !assignedUser?.id ||
            assignedUser?.id === currentUserId ||
            actionRequestLoading
          }
          onClick={e => {
            e.stopPropagation();
            onActionRequest();
          }}
        >
          Request
        </Button>
      </ButtonTooltipArea>
    </Tooltip>
  );
};

const ButtonTooltipArea = styled("div")`
  display: inline-block;
`;
